import { redirect } from "next/navigation";
import { giftCardItem } from "@/utils/giftCardUtils";
import getMerchantDetails from "@/dataLayer/getMerchantDetails";
import getItemDetails from "@/dataLayer/getItemDetails";
import { Item } from "@/dto/item";
import { ROUTE_GIFTCARD, ROUTE_NOT_FOUND } from "@/utils/constants";

const ITEM_ID_LENGTH = 13;
const itemIdRegexPattern = `^[A-Z0-9]{${ITEM_ID_LENGTH}}$`;
export const itemIdRegex = new RegExp(itemIdRegexPattern);
type OptionalSlugParams = Promise<{ slug: string }> | undefined;

export const slugifyName = (name: string): string => {
  if (!name) {
    return "";
  }

  // Replace whitespace with a single hyphen
  let slug = name.trim().replace(/\s+/g, "-");

  // Normalize any accented characters and remove non-url safe characters
  slug = slug
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove combining diacritical marks
    .replace(/[^a-zA-Z0-9-]/g, ""); // Remove remaining non-latin/special characters

  // Convert to lowercase
  slug = slug.toLowerCase();

  // Replace multiple consecutive hyphens with a single one
  slug = slug.replace(/-+/g, "-");

  // Remove any leading or trailing hyphens
  slug = slug.replace(/^-+|-+$/g, "");

  return slug;
};

export const getItemId = (slug: string) => {
  const decodedSlug = decodeURIComponent(slug);
  let parts;
  if (!decodedSlug.includes("-") && decodedSlug.includes("/")) {
    parts = decodedSlug.split("/");
    return parts.at(-1) || "";
  }
  parts = decodedSlug.split("-");
  return parts.at(-1) || "";
};

export const getValidItemIdFromSlug = (slug: string): string | null => {
  const itemId = getItemId(slug);

  return itemIdRegex.test(itemId) ? itemId : null;
};

export const getItemUrl = (item: any) => {
  if (item.id === giftCardItem.id) {
    return ROUTE_GIFTCARD;
  }

  if (item.items && item.items.length > 0) {
    item = item.items[0];
  }

  if (item.itemUrl) {
    const parts = item.itemUrl.split("/");
    return encodeURIComponent(`${parts[parts.length - 1]}`);
  }

  return slugifyName(item.name)
    ? encodeURIComponent(`${slugifyName(item.name)}-${item.id}`)
    : item.id;
};

export const getItemUrlById = async (itemId: string) => {
  if (itemId === giftCardItem.id) {
    return ROUTE_GIFTCARD;
  }
  if (!itemIdRegex.test(itemId)) {
    return null;
  }

  const merchant = await getMerchantDetails();
  let item = await getItemDetails(merchant.id, itemId);

  if (item && item.items?.length > 0) {
    item = item.items[0] as any;
  }

  if (item?.itemUrl) {
    const parts = item.itemUrl.split("/");
    return encodeURIComponent(`${parts.at(-1) || ""}`);
  }

  if (item) {
    return slugifyName(item.name)
      ? encodeURIComponent(`${slugifyName(item.name)}-${itemId}`)
      : itemId;
  } else {
    return null;
  }
};

export const getItemUrlByPathname = (pathname: string) => {
  const parts = pathname.split("/");
  return encodeURIComponent(`${parts.at(-1) || ""}`);
};

export const resolveSlugParams = async (
  params: OptionalSlugParams
): Promise<void> => {
  const awaitedParams = params === undefined ? { slug: "all" } : await params;

  if (awaitedParams.slug !== "all") {
    if (getValidItemIdFromSlug(awaitedParams.slug) === null) {
      redirect(ROUTE_NOT_FOUND);
    }
  }
};

/**
 * Handles server-side redirects for item pages to ensure proper URL formatting and consistency.
 *
 * This function checks if the current item URL matches the canonical URL format and redirects
 * if necessary. It handles two main scenarios:
 * 1. When the slug doesn't contain a hyphen (likely just an item ID)
 * 2. When the slug contains a hyphen (likely a name-itemId format)
 *
 * @param pageType - The type of page being processed (e.g., "item")
 * @param slug - The URL slug from the current request, may be just an ID or name-ID format
 * @param itemId - The extracted item ID from the slug
 * @param basePath - The base path to prepend to the redirect URL
 * @param itemDetails - Optional item details response object to avoid additional API calls
 * @returns Promise<void> - Performs a redirect if the current URL doesn't match the canonical format
 *
 * @example
 * // Redirects from /ABC123DEF4567 to /product-name-ABC123DEF4567
 * await handleServerSideItemRedirect("item", "ABC123DEF4567", "ABC123DEF4567", "/");
 *
 * @example
 * // Redirects if the slug format doesn't match the canonical URL
 * await handleServerSideItemRedirect("item", "old-name-ABC123DEF4567", "ABC123DEF4567", "/", itemDetails);
 */
export const handleServerSideItemRedirect = async (
  pageType: string,
  slug: string,
  itemId: string,
  basePath: string
) => {
  if (pageType === "item" && !slug.includes("-")) {
    const itemUrl = await getItemUrlById(slug);
    if (itemUrl != null && slug !== itemUrl) {
      return redirect(`${basePath}/${itemUrl}`);
    }
  }
  if (pageType === "item" && slug.includes("-")) {
    const itemUrl = await getItemUrlById(itemId);
    if (`${slug}` !== itemUrl && itemUrl != null) {
      return redirect(`${basePath}/${itemUrl}`);
    }
  }
};

interface GenerateItemUrlParams {
  item: Item;
  baseRoute: string;
  queryParams?: string;
}

export const generateItemUrl = ({
  item,
  baseRoute,
  queryParams = "",
}: GenerateItemUrlParams): string => {
  if (item.id === giftCardItem.id) {
    return ROUTE_GIFTCARD;
  }

  const itemUrl = getItemUrl(item);
  const url = `${baseRoute}/${itemUrl}${queryParams}`;

  return url;
};
